import { Slider as BaseSlider } from '@base-ui/react/slider';
import React, { useCallback, useMemo, useState } from 'react';

import { Icon } from '../../assets/Icon/Icon';
import { fade, palette } from '../../common/colors';
import { outlineColors, outlines } from '../../common/outlines';
import { TextInput } from '../../components/TextInput/TextInput';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { selectors, transitions } from '../shared/styles';

const sliderValues = {
  thumbSize: 14,
};

const SliderMarker = styled(Small, {
  display: 'flex',
  justifyContent: 'center',
  color: colors.controlContentAccentLight,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: colors.controlContentAccentDark,
  },

  [selectors.hover]: {
    color: colors.controlContentValueLight,

    [darkThemeSelector]: {
      color: colors.controlContentValueDark,
    },
  },

  variants: {
    isMin: {
      true: {
        left: 0,
        justifyContent: 'flex-start',
      },
      false: {},
    },
    isMax: {
      true: {
        right: 0,
        justifyContent: 'flex-end',
        marginLeft: `$${sliderValues.thumbSize / 2}`,
      },
      false: {},
    },
    isSelected: {
      true: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },
      },
      false: {},
    },
    positioned: {
      absolute: {
        position: 'absolute',
        top: 0,
        width: 0,
      },
      relative: {
        position: 'relative',
        minWidth: '$16',
      },
    },
  },
});
SliderMarker.displayName = 'SliderMarker';

const SliderMarkers = styled('div', {
  position: 'relative',
  width: '100%',
  heightAll: '$16',
  margin: '$8 0 -$8',
});
SliderMarkers.displayName = 'SliderMarkers';

const SliderThumbIcon = styled(Icon, {
  widthAll: '$8',
  heightAll: '$8',
  color: '$$dotColor',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: '$$dotColor',
  },
});
SliderThumbIcon.displayName = 'SliderThumbIcon';

const SliderThumb = styled(BaseSlider.Thumb, {
  position: 'absolute',
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: `$${sliderValues.thumbSize}`,
  height: `$${sliderValues.thumbSize}`,
  background: colors.bgApplicationLight,
  outline: outlines.control,
  outlineColor: outlineColors.control.raisedInitialLight,
  boxShadow: shadows.controlRaisedInitialLight,
  borderRadius: '$4',
  transition: transitions.control,
  userSelect: 'none',
  cursor: 'grab',

  $$dotColor: colors.gray200,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    outlineColor: outlineColors.control.raisedInitialDark,
    boxShadow: shadows.controlRaisedInitialDark,

    $$dotColor: colors.gray600,
  },

  [selectors.hover]: {
    outlineColor: outlineColors.control.raisedHoveredLight,
    boxShadow: shadows.controlRaisedHoveredLight,

    $$dotColor: colors.gray300,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.raisedHoveredDark,
      boxShadow: shadows.controlRaisedHoveredDark,

      $$dotColor: colors.gray500,
    },
  },

  [selectors.focus]: {
    outlineColor: outlineColors.control.raisedFocusedLight,
    boxShadow: shadows.controlRaisedFocusedLight,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.raisedFocusedDark,
      boxShadow: shadows.controlRaisedFocusedDark,
    },
  },

  '&[data-disabled]': {
    outlineColor: outlineColors.control.raisedDisabledLight,
    boxShadow: shadows.controlRaisedDisabledLight,

    $$dotColor: colors.gray200,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.raisedDisabledDark,
      boxShadow: shadows.controlRaisedDisabledDark,

      $$dotColor: colors.gray600,
    },
  },
});
SliderThumb.displayName = 'SliderThumb';

const SliderControl = styled(BaseSlider.Control, {
  position: 'relative',
  zIndex: 2,
  width: '100%',
  height: '$24',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});
SliderControl.displayName = 'SliderControl';

const SliderTrack = styled(BaseSlider.Track, {
  position: 'relative',
  width: '100%',
  height: '$2',
  backgroundColor: colors.tokenBgNeutralLight,
  borderRadius: '$8',

  [darkThemeSelector]: {
    backgroundColor: colors.tokenBgNeutralDark,
  },
});
SliderTrack.displayName = 'SliderTrack';

const SliderIndicator = styled(BaseSlider.Indicator, {
  height: 'inherit',
  backgroundColor: colors.brand600,
  borderRadius: '99em',
  pointerEvents: 'none',
  transition: transitions.control,

  '&[data-disabled]': {
    backgroundColor: colors.brand300,

    [darkThemeSelector]: {
      backgroundColor: colors.brand800,
    },
  },
});
SliderIndicator.displayName = 'SliderIndicator';

const SliderFields = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
});
SliderFields.displayName = 'SliderFields';

const SliderArea = styled(BaseSlider.Root, {
  position: 'relative',
  width: '100%',
});
SliderArea.displayName = 'SliderArea';

const SliderAreaContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  padding: '0 $12',
  borderRadius: '$8',
  background: colors.bgApplicationLight,
  strokeAll: fade(palette.controlStrokeBaseLight, 0.12),

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    strokeAll: fade(palette.controlStrokeBaseDark, 0.44),
  },

  variants: {
    disabled: {
      true: {
        background: colors.bgNeutralLight,
        strokeAll: fade(palette.controlStrokeBaseLight, 0.3),
        opacity: 0.5,
        pointerEvents: 'none',

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
          strokeAll: fade(palette.controlStrokeBaseDark, 0.36),
        },
      },
    },

    // Drops the raised surface so the slider can sit inside another control's
    // chrome without nesting a second background and outline inside it.
    bare: {
      true: {
        padding: 0,
        borderRadius: 0,
        background: 'none',
        strokeAll: 'transparent',

        [darkThemeSelector]: {
          background: 'none',
          strokeAll: 'transparent',
        },
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      bare: true,
      disabled: true,
      css: {
        background: 'none',
        strokeAll: 'transparent',

        [darkThemeSelector]: {
          background: 'none',
          strokeAll: 'transparent',
        },
      },
    },
  ],
});
SliderAreaContainer.displayName = 'SliderAreaContainer';

const SliderContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  width: '100%',
  userSelect: 'none',
  touchAction: 'none',
});
SliderContainer.displayName = 'SliderContainer';

const SliderTooltip = styled(Small, {
  position: 'absolute',
  zIndex: 10,
  bottom: '100%',
  left: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: 'translateX(-50%)',
  minWidth: '$24',
  padding: '$2 $4',
  marginBottom: '$3',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$8',
  opacity: 0,
  color: colors.bodyBrandLight,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  userSelect: 'none',
  transition: transitions.control,

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
    color: colors.bodyBrandDark,
  },

  '&[data-visible="true"]': {
    opacity: 1,
  },
});
SliderTooltip.displayName = 'SliderTooltip';

export type SliderProps = {
  /**
   * The current value(s) of the slider.
   */
  value?: number[];
  /**
   * The default value(s) of the slider.
   */
  defaultValue?: number[];
  /**
   * Callback fired when the value changes.
   */
  onValueChange?: (value: number[]) => void;
  /**
   * The minimum value of the slider.
   */
  min?: number;
  /**
   * The maximum value of the slider.
   */
  max?: number;
  /**
   * The step value of the slider.
   */
  step?: number;
  /**
   * Whether the slider is disabled.
   */
  disabled?: boolean;
  /**
   * Whether to show the fields for the slider values.
   */
  showFields?: boolean;
  /**
   * Whether to draw the slider's own surface. Turn it off to embed the slider
   * in another control, where a second background and outline would nest.
   *
   * @default true
   */
  showContainer?: boolean;
  /**
   * The markers you want to show along the slider.
   */
  markers?: {
    /**
     * The marker you want to show along the slider at the min value.
     */
    min?: React.ReactNode;
    /**
     * The markers you want to show along the slider between the min and max.
     */
    mid?: number[] | React.ReactNode[];
    /**
     * The marker you want to show along the slider at the max value.
     */
    max?: React.ReactNode;
  };
  /**
   * Whether to show the markers on the slider.
   */
  showMarkers?: boolean;
  /**
   * Controls when tooltips are shown for the slider values.
   * - `never`: Disables tooltips entirely
   * - `interaction`: Shows tooltips when hovering or focusing (default behavior)
   * - `always`: Shows tooltips permanently
   */
  showTooltips?: 'never' | 'interaction' | 'always';
  /**
   * The unit of the slider values (e.g. '%', 'Kbps', etc.).
   */
  unit?: React.ReactNode;
  /**
   * Names the slider for assistive technology. Pass this whenever the slider is not
   * inside a field — a bare slider is otherwise an unnamed `<input type="range">`.
   *
   * `PrimaryField`/`SecondaryField` supply `aria-labelledby` themselves, so a slider
   * passed as their `element` does not need either prop.
   */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
};

/**
 * Distinguishes the ends of a range slider. Returns `null` for a single thumb, which
 * needs no qualifying, so callers can treat that as "just use the name as given".
 */
function thumbPosition(index: number, count: number): string | null {
  if (count < 2) return null;
  if (count === 2) return index === 0 ? 'minimum' : 'maximum';
  return `value ${index + 1}`;
}

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>((props, forwardedRef) => {
  const {
    disabled,
    onValueChange,
    markers,
    min = 0,
    max = 100,
    step = 1,
    showFields,
    showMarkers,
    showContainer = true,
    showTooltips = 'always',
    unit,
    value,
    defaultValue,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  } = props;

  const [isHovered, setHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleMouseEnter = () => setHovered(true);
  const handleMouseLeave = () => setHovered(false);
  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const currentValue = useMemo(() => value ?? defaultValue ?? [min], [value, defaultValue, min]);
  const isMin = currentValue[0] === min;
  const isMax = currentValue[currentValue.length - 1] === max;
  const range = max - min;

  // Stable keys per thumb slot — derived from count only so keys don't change as values change.
  // Mapping over thumbKeys (strings) instead of currentValue means `key=` never uses the
  // .map() index parameter, which satisfies react/no-array-index-key.
  const thumbKeys = useMemo(
    () => Array.from({ length: currentValue.length }, (_, i) => `thumb-${i}`),
    [currentValue.length],
  );

  const constrainValue = useCallback(
    (inputValue: number, index: number, values: number[]): number => {
      const boundedValue = Math.min(Math.max(inputValue, min), max);

      if (values.length === 1) return boundedValue;

      const prevValue = index > 0 ? values[index - 1] : -Infinity;
      const nextValue = index < values.length - 1 ? values[index + 1] : Infinity;

      return Math.min(Math.max(boundedValue, prevValue), nextValue);
    },
    [min, max],
  );

  const handleMarkerClick = useCallback(
    (markerValue: number) => {
      if (disabled || !onValueChange) return;

      const distances = currentValue.map((val) => Math.abs(val - markerValue));
      const closestIndex = distances.indexOf(Math.min(...distances));

      const newValues = [...currentValue];
      newValues[closestIndex] = constrainValue(markerValue, closestIndex, currentValue);
      onValueChange(newValues);
    },
    [disabled, onValueChange, currentValue, constrainValue],
  );

  const handleValueChange = useCallback(
    (index: number) => (inputValue: string) => {
      if (!onValueChange) return;

      const num = Number(inputValue);
      if (Number.isNaN(num)) return;

      const newValues = [...currentValue];
      newValues[index] = constrainValue(num, index, currentValue);
      onValueChange(newValues);
    },
    [onValueChange, currentValue, constrainValue],
  );

  // Base UI's `Slider.Thumb` and our `TextInput` both forward these onto the real
  // `<input>`, so this is what actually names the control for assistive technology.
  // Every thumb of a range slider would otherwise share one name, so qualify the
  // literal label per thumb; an `aria-labelledby` points at markup we can't extend,
  // so those thumbs keep the shared name rather than losing it.
  const namingFor = useCallback(
    (index: number) => {
      const position = thumbPosition(index, currentValue.length);
      const describedBy = ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {};

      if (ariaLabel) {
        return {
          'aria-label': position ? `${ariaLabel} ${position}` : ariaLabel,
          ...describedBy,
        };
      }
      if (ariaLabelledBy) return { 'aria-labelledby': ariaLabelledBy, ...describedBy };
      return position ? { 'aria-label': position, ...describedBy } : describedBy;
    },
    [ariaLabel, ariaLabelledBy, ariaDescribedBy, currentValue.length],
  );

  const shouldShowTooltip = showTooltips !== 'never';
  const isTooltipVisible =
    showTooltips === 'always' || (showTooltips === 'interaction' && (isHovered || isFocused));

  return (
    <SliderContainer>
      {/* The scale markers and thumb tooltips sit outside the slider's own group, so the
          disabled state has to be exposed here to cover them. Base UI only emits
          `data-disabled`, which conveys nothing to assistive technology. */}
      <SliderAreaContainer
        role="group"
        aria-disabled={disabled || undefined}
        bare={!showContainer}
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {showMarkers && (
          <SliderMarkers>
            <SliderMarker
              positioned="absolute"
              isMin
              isSelected={isMin}
              onClick={() => handleMarkerClick(min)}
            >
              {markers?.min ?? `${min}${unit ?? ''}`}
            </SliderMarker>
            {markers?.mid?.map((marker, index) => {
              const markerValue = typeof marker === 'number' ? marker : min + index + 1;
              const position = ((markerValue - min) / range) * 100;
              return (
                <SliderMarker
                  key={`markers-mid-${markerValue}`}
                  positioned="absolute"
                  isSelected={currentValue.includes(markerValue)}
                  css={{
                    left: `calc(${position}%)`,
                  }}
                  onClick={() => handleMarkerClick(markerValue)}
                >
                  {marker}
                  {unit}
                </SliderMarker>
              );
            })}
            <SliderMarker
              positioned="absolute"
              isMax
              isSelected={isMax}
              onClick={() => handleMarkerClick(max)}
            >
              {markers?.max ?? `${max}${unit ?? ''}`}
            </SliderMarker>
          </SliderMarkers>
        )}
        <SliderArea
          min={min}
          max={max}
          step={step}
          value={currentValue}
          onValueChange={(val) => onValueChange?.(Array.isArray(val) ? val : [val])}
          disabled={disabled}
          ref={forwardedRef}
        >
          <SliderControl>
            <SliderTrack>
              <SliderIndicator />
              {thumbKeys.map((thumbKey, index) => (
                <SliderThumb
                  key={thumbKey}
                  index={index}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  {...namingFor(index)}
                >
                  <SliderThumbIcon icon="slide-horizontal" />
                  {shouldShowTooltip && (
                    <SliderTooltip data-visible={isTooltipVisible}>
                      {currentValue[index]}
                      {unit}
                    </SliderTooltip>
                  )}
                </SliderThumb>
              ))}
            </SliderTrack>
          </SliderControl>
        </SliderArea>
      </SliderAreaContainer>
      {showFields && (
        <SliderFields>
          {thumbKeys.map((thumbKey, i) => (
            <TextInput
              key={thumbKey}
              disabled={disabled}
              type="number"
              width="100%"
              value={`${currentValue[i]}`}
              onChange={handleValueChange(i)}
              suffix={unit}
              {...namingFor(i)}
            />
          ))}
        </SliderFields>
      )}
    </SliderContainer>
  );
});
